import { cn } from '@rocco/utils/cn';
import { ReactComponent as CheckIcon } from '@rocco/icons/svg/check.svg';
import { ReactComponent as PlusIcon } from '@rocco/icons/svg/plus.svg';

import {
    useBulkEnquirySelection,
    type BulkEnquiryItem,
} from './bulk-enquiry-state';

interface BulkEnquireSelectControlProps {
    item: BulkEnquiryItem;
    className?: string;
}

/**
 * The "add to enquiry" tick overlaid on a browse tile — a multi-select
 * affordance deliberately distinct from the Save heart (which sits top-right on
 * the marketplace tiles), so it reads top-left. Toggling adds/removes the tile
 * from the bulk-enquiry selection. The whole tile is a link, so the click is
 * swallowed (preventDefault + stopPropagation) to select instead of navigate.
 * Always visible (it only renders in bulk-enquiry mode), so no hover reveal.
 */
export const BulkEnquireSelectControl = ({
    item,
    className,
}: BulkEnquireSelectControlProps) => {
    const { isSelected, toggle } = useBulkEnquirySelection();
    const selected = isSelected(item.siteTreeID);

    const toggleClick = (event: React.MouseEvent) => {
        // The tile is a RoccoLink — select instead of following it.
        event.preventDefault();
        event.stopPropagation();
        toggle(item);
    };

    return (
        <>
            {/* Selected wash across the whole image, so the state reads from
                grid distance — the corner tick alone is too quiet now that
                the entire tile is the select target. */}
            <div
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-0 z-10 bg-black/25 transition-opacity duration-200',
                    selected ? 'opacity-100' : 'opacity-0',
                )}
            />

            {/* Corner add control — the unselected affordance. */}
            <button
                type="button"
                aria-hidden={selected}
                tabIndex={selected ? -1 : 0}
                aria-label={`Add ${item.title} to your enquiry`}
                onClick={toggleClick}
                className={cn(
                    'absolute top-3 left-3 z-20 flex size-8 items-center justify-center rounded-full border border-white/80 bg-black/25 text-white backdrop-blur-sm transition hover:bg-black/40 active:bg-black/40',
                    selected && 'pointer-events-none opacity-0',
                    className,
                )}
            >
                <PlusIcon className="size-4 fill-current" />
            </button>

            {/* Centred tick — the selected state, scaled up over the wash. */}
            <button
                type="button"
                aria-hidden={!selected}
                tabIndex={selected ? 0 : -1}
                aria-label={`Remove ${item.title} from your enquiry`}
                onClick={toggleClick}
                className={cn(
                    'absolute top-1/2 left-1/2 z-20 flex size-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#181818] shadow-[0_6px_20px_rgba(0,0,0,0.25)] transition-[opacity,scale] duration-200',
                    selected
                        ? 'scale-100 opacity-100'
                        : 'pointer-events-none scale-75 opacity-0',
                )}
            >
                <CheckIcon className="size-5 fill-current" />
            </button>
        </>
    );
};
